import React from 'react'
import confetti from 'canvas-confetti'
import { toast } from 'sonner'

/**
 * Confetti: one fullscreen canvas + single confetti instance (no per-burst DOM churn).
 * Day finale cancels prior finale timeouts / rAF so rapid replays do not stack work.
 */

/** Optional short chime — set true to experiment with Web Audio (off by default). */
const ENABLE_COMPLETION_CHIME = false

const LIOS_CONFETTI_ROOT_ID = 'lios-confetti-root'

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)]!
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function isDocumentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

/** Brand-aligned palette — mutable array reused by shots (no spread per frame). */
const CONFETTI_COLOR_VALUES: string[] = [
  '#0284c7',
  '#38bdf8',
  '#0ea5e9',
  '#7dd3fc',
  '#f472b6',
  '#38bdf8',
  '#fbbf24',
  '#34d399',
]

const HABIT_BURST_COLORS: string[] = [
  '#34d399',
  '#6ee7b7',
  '#a7f3d0',
  '#0284c7',
  '#38bdf8',
]

type ConfettiShot = ReturnType<typeof confetti.create>

let confettiApi: ConfettiShot | null = null

/** Cancels overlapping day-finale timers and animation frames (prevents stacked rAF loops). */
let finaleRunId = 0
let finaleRafId = 0
const finaleTimeoutIds: number[] = []

function abortOngoingFinale(): void {
  if (finaleRafId !== 0) {
    cancelAnimationFrame(finaleRafId)
    finaleRafId = 0
  }
  for (const id of finaleTimeoutIds) {
    window.clearTimeout(id)
  }
  finaleTimeoutIds.length = 0
  finaleRunId += 1
}

function getConfettiApi(): ConfettiShot | null {
  if (typeof document === 'undefined' || prefersReducedMotion()) return null
  if (confettiApi) return confettiApi

  let root = document.getElementById(LIOS_CONFETTI_ROOT_ID) as HTMLDivElement | null
  if (!root) {
    root = document.createElement('div')
    root.id = LIOS_CONFETTI_ROOT_ID
    root.setAttribute('aria-hidden', 'true')
    root.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483646;overflow:hidden;'
    document.body.appendChild(root)
  }

  let canvas = root.querySelector('canvas') as HTMLCanvasElement | null
  if (!canvas) {
    canvas = document.createElement('canvas')
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
    root.appendChild(canvas)
  }

  confettiApi = confetti.create(canvas, { resize: true, useWorker: true })
  return confettiApi
}

function fire(shot: (api: ConfettiShot) => void): void {
  if (isDocumentHidden()) return
  const api = getConfettiApi()
  if (!api) return
  shot(api)
}

/** Satisfying pop when a single task is checked off */
export function burstTaskComplete(): void {
  fire((api) => {
    void api({
      particleCount: 95,
      spread: 88,
      startVelocity: 38,
      gravity: 0.95,
      drift: 0.04,
      ticks: 220,
      scalar: 1.05,
      origin: { x: 0.5, y: 0.78 },
      colors: CONFETTI_COLOR_VALUES,
    })
  })
  fire((api) => {
    void api({
      particleCount: 35,
      spread: 100,
      startVelocity: 22,
      gravity: 1.1,
      ticks: 180,
      scalar: 0.75,
      origin: { x: 0.5, y: 0.82 },
      colors: CONFETTI_COLOR_VALUES,
    })
  })
}

/** Full “finale” when the day’s task list is cleared */
export function burstDayComplete(): void {
  if (isDocumentHidden()) return
  const api = getConfettiApi()
  if (!api) return

  abortOngoingFinale()
  const run = finaleRunId

  const left = {
    particleCount: 140,
    spread: 72,
    startVelocity: 58,
    angle: 60,
    origin: { x: 0, y: 0.62 },
    colors: CONFETTI_COLOR_VALUES,
    scalar: 1.1,
    ticks: 280,
  }
  const right = {
    ...left,
    angle: 120,
    origin: { x: 1, y: 0.62 },
  }
  void api(left)
  void api(right)

  finaleTimeoutIds.push(
    window.setTimeout(() => {
      if (run !== finaleRunId) return
      void api({
        particleCount: 160,
        spread: 360,
        startVelocity: 38,
        origin: { x: 0.5, y: 0.45 },
        colors: CONFETTI_COLOR_VALUES,
        scalar: 1.05,
        ticks: 300,
      })
    }, 160)
  )

  finaleTimeoutIds.push(
    window.setTimeout(() => {
      if (run !== finaleRunId) return
      void api({
        particleCount: 90,
        spread: 100,
        startVelocity: 48,
        origin: { x: 0.2, y: 0.35 },
        colors: CONFETTI_COLOR_VALUES,
      })
      void api({
        particleCount: 90,
        spread: 100,
        startVelocity: 48,
        origin: { x: 0.8, y: 0.35 },
        colors: CONFETTI_COLOR_VALUES,
      })
    }, 320)
  )

  const end = Date.now() + 2400
  const tick = () => {
    if (run !== finaleRunId) return
    void api({
      particleCount: 2,
      angle: 60,
      spread: 48,
      origin: { x: 0 },
      colors: CONFETTI_COLOR_VALUES,
      startVelocity: 52,
      scalar: 0.9,
    })
    void api({
      particleCount: 2,
      angle: 120,
      spread: 48,
      origin: { x: 1 },
      colors: CONFETTI_COLOR_VALUES,
      startVelocity: 52,
      scalar: 0.9,
    })
    if (Date.now() < end) {
      finaleRafId = window.requestAnimationFrame(tick)
    } else {
      finaleRafId = 0
    }
  }
  finaleRafId = window.requestAnimationFrame(tick)
}

/** Quick sparkle when a habit cell is checked */
export function burstHabitComplete(): void {
  fire((api) => {
    void api({
      particleCount: 55,
      spread: 62,
      startVelocity: 32,
      gravity: 0.9,
      origin: { x: 0.5, y: 0.7 },
      colors: HABIT_BURST_COLORS,
      scalar: 0.95,
      ticks: 200,
    })
  })
}

const TASK_LINES = [
  'Nice work!',
  'One down — keep the streak.',
  'That’s momentum.',
  'Checked off. What’s next?',
  'Small wins add up.',
] as const

const HABIT_LINES = [
  'Habit locked in.',
  'Consistency wins.',
  'Showing up counts.',
  'Another rep in the bank.',
  'Steady progress.',
] as const

const DAY_COMPLETE_BODY = [
  'Every task done. Take a breath — you earned it.',
  'You cleared the list. Enjoy the quiet.',
  'Full sweep today. Well played.',
] as const

function playCompletionChime(): void {
  if (!ENABLE_COMPLETION_CHIME || typeof window === 'undefined') return
  try {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = 523.25
    gain.gain.setValueAtTime(0.08, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
    osc.start(ctx.currentTime)
    osc.stop(ctx.currentTime + 0.25)
    void ctx.close()
  } catch {
    /* ignore */
  }
}

function truncateTitle(title: string, max = 56): string {
  const t = title.trim()
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

export function celebrateTaskCompleted(title: string): void {
  const line = pick(TASK_LINES)
  burstTaskComplete()
  toast.custom(
    () => (
      <div className="lios-celebrate-card lios-celebrate-card--task" role="status">
        <div className="lios-celebrate-card-glow lios-celebrate-card-glow--task" aria-hidden />
        <div className="lios-celebrate-card-inner">
          <span className="lios-celebrate-emoji" aria-hidden>
            ✨
          </span>
          <p className="lios-celebrate-kicker">Task complete</p>
          <p className="lios-celebrate-headline">{line}</p>
          <p className="lios-celebrate-detail">{truncateTitle(title)}</p>
        </div>
      </div>
    ),
    { duration: 4200 }
  )
  playCompletionChime()
}

export function celebrateHabitCompleted(name: string): void {
  const line = pick(HABIT_LINES)
  burstHabitComplete()
  toast.custom(
    () => (
      <div className="lios-celebrate-card lios-celebrate-card--habit" role="status">
        <div className="lios-celebrate-card-glow lios-celebrate-card-glow--habit" aria-hidden />
        <div className="lios-celebrate-card-inner">
          <span className="lios-celebrate-emoji" aria-hidden>
            🌿
          </span>
          <p className="lios-celebrate-kicker">Habit done</p>
          <p className="lios-celebrate-headline">{line}</p>
          <p className="lios-celebrate-detail lios-celebrate-detail--habit-name">
            {truncateTitle(name)}
          </p>
          <span className="lios-celebrate-badge">Logged for today</span>
        </div>
      </div>
    ),
    { duration: 4500 }
  )
  playCompletionChime()
}

/**
 * Fullscreen, front-and-center celebration for clearing the day.
 * We skip Sonner here so we can command the entire viewport (backdrop + large card).
 */
const DAY_OVERLAY_ID = 'lios-day-complete-overlay'
const DAY_OVERLAY_DURATION_MS = 6000

let dayOverlayCloseTimer = 0
let dayOverlayRemoveTimer = 0
let dayOverlayKeyHandler: ((e: KeyboardEvent) => void) | null = null

function teardownDayOverlay(immediate = false): void {
  if (dayOverlayCloseTimer !== 0) {
    window.clearTimeout(dayOverlayCloseTimer)
    dayOverlayCloseTimer = 0
  }
  if (dayOverlayRemoveTimer !== 0) {
    window.clearTimeout(dayOverlayRemoveTimer)
    dayOverlayRemoveTimer = 0
  }
  if (dayOverlayKeyHandler) {
    window.removeEventListener('keydown', dayOverlayKeyHandler)
    dayOverlayKeyHandler = null
  }
  const existing = document.getElementById(DAY_OVERLAY_ID)
  if (!existing) return
  if (immediate) {
    existing.remove()
    return
  }
  existing.setAttribute('data-closing', 'true')
  dayOverlayRemoveTimer = window.setTimeout(() => {
    existing.remove()
    dayOverlayRemoveTimer = 0
  }, 320)
}

function showDayCompleteOverlay(): void {
  if (typeof document === 'undefined') return

  teardownDayOverlay(true)

  const overlay = document.createElement('div')
  overlay.id = DAY_OVERLAY_ID
  overlay.className = 'lios-day-overlay'
  overlay.setAttribute('role', 'status')
  overlay.setAttribute('aria-live', 'polite')

  const body = pick(DAY_COMPLETE_BODY)
  overlay.innerHTML = `
    <button
      type="button"
      class="lios-day-overlay-backdrop"
      aria-label="Dismiss celebration"
    ></button>
    <div class="lios-day-overlay-card" role="dialog" aria-modal="false" aria-label="Day complete">
      <div class="lios-day-overlay-glow" aria-hidden="true"></div>
      <div class="lios-day-overlay-sparkles" aria-hidden="true">
        <span>✦</span><span>✦</span><span>✦</span><span>✦</span><span>✦</span>
      </div>
      <div class="lios-day-overlay-inner">
        <span class="lios-day-overlay-emoji" aria-hidden="true">🏆</span>
        <p class="lios-day-overlay-kicker">All clear</p>
        <p class="lios-day-overlay-headline">Day complete</p>
        <p class="lios-day-overlay-body"></p>
        <button type="button" class="lios-day-overlay-dismiss">Nice</button>
      </div>
    </div>
  `

  const bodyEl = overlay.querySelector<HTMLElement>('.lios-day-overlay-body')
  if (bodyEl) bodyEl.textContent = body

  const close = () => teardownDayOverlay()
  overlay
    .querySelector<HTMLButtonElement>('.lios-day-overlay-backdrop')
    ?.addEventListener('click', close)
  overlay
    .querySelector<HTMLButtonElement>('.lios-day-overlay-dismiss')
    ?.addEventListener('click', close)

  dayOverlayKeyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close()
  }
  window.addEventListener('keydown', dayOverlayKeyHandler)

  document.body.appendChild(overlay)

  dayOverlayCloseTimer = window.setTimeout(close, DAY_OVERLAY_DURATION_MS)
}

export function celebrateDayComplete(): void {
  burstDayComplete()
  showDayCompleteOverlay()
  playCompletionChime()
}

/** Reserved for future: no toast when uncompleting. */
export function celebrateTaskUndone(_title: string): void {
  void _title
}

/** Reserved for future: no toast when unchecking a habit. */
export function celebrateHabitUndone(_name: string): void {
  void _name
}
